import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { Search } from 'lucide-react'; // Using lucide-react for the icon
import 'swiper/css';
import '../styles/Rental.css';

const rentalEquipmentData = [
  {
    category: "Primary Injection testing equipment",
    items: [
      "Programma | ODEN AT,3H",
      "Omicron | CPC 100",
      
      "ISA | KAM 5/12.5-G DIG HOLD"
    ]
  },
  {
    category: "Relay testing equipment",
    items: [
      "Programma | Sverker 750 , 760 , 780",
      "Programma | Freja 300 , 306, 406",
      "Omicron | CMC 256 PLUS , 356",
      "Omicron | CMGPS588",
      "ISA | DRTS 64",
    ]
  },
  {
    category: "High voltage testing equipment",
    items: [
      "Kilovolt | KPG 110kv",
      "Automatic Electric | 5kV",
      "Automatic Electric | 25kV",
      "Phenix | 6CP100/50-7.5",
      "Phenix | 6CP50/10-3",
      "b2 High-Voltage | HVA60",
      "b2 High-Voltage | HVA90",
      "Fluke | 80K-40",
      // "TECPEL | HVP275",
      "b2 High Voltage | HVA 45",
      "Phenix | 6CP100/50-10.0",
    ]
  },
  {
    category: "Transformer testing equipment",
    items: [
      "Doble | M5400",
      "Omicron | CPC 100",
      "Omicron | CP TD1",
      "Omicron | CP SB1",
      "Scope T&M | TRM-104",
      "INSCIN | ISI-094B",
      "DV Power | TWA40D",
      "DV Power | TRT63A",
      "Cole Parmer | Polystat 2W24",
      "B2HV  | BA100",
      "BAUR DTA | 100C",
      "MEGGER OTS | 100AF/2",
    ]
  },
  {
    category: "Clamp Meter",
    items: [
      "Fluke | 355",
      "Motwane | DCM-10A",
      // "Lutron | CM-9930",
      "Chauvin Arnoux | MINI 05",
      "Waco | DT-2250",
      "Kyoritsu | 2010 Mini"
    ]
  },
  {
    category: "Multimeter",
    items: [
      "Fluke | 83v",
      "Fluke | 87v",
      "Sunwa | YX-360TR",
      // "Rishab | Multi 14S",
      "AVO | AVO 8",
      "Fluke | 115",
      "Fluke | 77 III",
      // "Metrahit | M205A"
    ]
  },
  {
    category: "Resistance meter",
    items: [
      "Megger | MIT525",
      "Megger | MIT515",
      "Scope | CRM 100c",
      "Programma | MOM 600A",
      "Megger | DLRO 10",
      "Programma | MOM200",
      "Hightest | HARE-300"
    ]
  },
  {
    category: "Analyser",
    items: [
      "Fluke(PQA) | 435-II , 438-II , 1775",
      "Programma(CB) | EGIL",
      "Omicron | CT Analyzer",
      "ISA(CB) | CBA 1000,3000"
    ]
  },
  {
    category: "Phase Rotation Indicator",
    items: [
      "Fluke | 9040",
      // "VKE | N/A",
      "Kyoritsu | 8031"
    ]
  },
  {
    category: "Other equipment",
    items: [
      "Letatwin | LM380E",
      "Amprobe | LCR55A",
      "Fluke | 1621",
      "Fluke | 1555",
      "Fluke | 1653B",
      "Motwane | DET-20",
      // "Waco | 150",
      "Programma | TM200",
      "Vitrek | V74",
      "Temporary DC Charger | 48v(70Amps),110v(60Amps),120v(100Amps,75Amps)",
      "Megger | Torkel 950",
      "Megger | Torkel 840",
      "Megger | TXL-870",
      "Fluke | Ti32",
      "BAUR | SHIRLA",
      "BAUR | Syscompact 2000",
      "Dimmersat | 20D-3P",
      "Anandhajothi | 6A",
      "Megger | DET4TC2",
      "Kyoritsu | KEW4106",
      "Fluke | 1664FC, 1653B"
    ]
  }
];

const Rental = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const handleClick = (model) => {
    navigate(`/select-rental/${encodeURIComponent(model)}`);
  };

  // Filter logic: Only shows categories that have items matching the search
  const filteredData = useMemo(() => {
    return rentalEquipmentData
      .map(group => ({
        ...group,
        items: group.items.filter(item => 
          item.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }))
      .filter(group => group.items.length > 0);
  }, [searchTerm]);

  return (
    <div className="rental-container-unique">
      {/* Header Flex Container */}
      <div className="rental-header-flex-unique">
        <h1 className="rental-title-unique">Our Rental Equipment</h1>
        
        {/* Search Bar on the Right */}
        <div className="rental-search-wrapper-unique">
          <div className="rental-search-bar-unique">
            <Search className="search-icon-unique" size={20} />
            <input 
              type="text" 
              placeholder="Search equipment..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {filteredData.length > 0 ? (
        filteredData.map((categoryGroup, groupIndex) => (
          <section key={groupIndex} className="rental-category-unique">
            <h2 className="rental-category-title-unique">{categoryGroup.category}</h2>

            <Swiper
              slidesPerView="auto"
              spaceBetween={20}
              loop={categoryGroup.items.length > 3} // Loop only if we have enough items
              autoplay={{ delay: 2500, disableOnInteraction: false }}
              modules={[Autoplay]}
              className="rental-swiper-unique"
            >
              {categoryGroup.items.map((item, index) => {
                const [brand, model] = item.split(' | ');
                const modelName = (model || brand).trim();
                return (
                  <SwiperSlide
                    key={index}
                    className="rental-slide-unique"
                    onClick={() => handleClick(modelName)}
                  >
                    {brand && <span className="rental-brand-unique">{brand.trim()}</span>}
                    <span className="rental-model-unique">{modelName}</span>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </section>
        ))
      ) : (
        <div className="no-results-unique">
          <p>No equipment matches your search "{searchTerm}"</p>
        </div>
      )}
    </div>
  );
};

export default Rental;